/**
 * @file Parent-board WIP Kanban renderers.
 */

import {
    collection,
    getDocs,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from '../../app/firebase/firebase.js';
import { state, appData } from '../../app/state/state.js';
import { showDetails } from '../../app/routing/navigationController.js';
import { openParentForm } from '../forms/parentFormModal.js';
import { loadMoreClosedItems as saveLoadMoreClosedItems } from '../api/parentItems.js';
import { renderParentWIPKanbanBoard } from './boardKanbanView.js';
import {
    createAssetCard_WIP,
    createPatientCard_WIP,
    createProjectCard_WIP,
} from './parentBoardCards.js';

async function loadMoreClosedItems(collectionType) {
    await saveLoadMoreClosedItems(collectionType);
}

async function fetchAllOpenTasksForParentType(parentType) {
    const allTasksQuery = query(
        collection(db, 'tasks'),
        where('parentType', '==', parentType),
        where('isSubtask', '==', true),
        where('isClosedResolved', '==', false),
    );
    const allTasksSnapshot = await getDocs(allTasksQuery);

    return allTasksSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }));
}

function openQuickAddTask(parentId) {
    openParentForm('task', { parentId, owner: state.currentUser?.uid });
}

/**
 * Renders the Patient WIP Kanban board.
 * @param {HTMLElement} board Board container.
 * @returns {Promise<void>}
 */
export async function renderPatientWIPKanban(board) {
    return renderParentWIPKanbanBoard(board, {
        viewKey: 'patients',
        itemType: 'patient',
        parentType: 'patient',
        items: appData?.patients || [],
        renderCard: (patient, openTasks) => createPatientCard_WIP(patient, openTasks),
        quickAddDatasetKey: 'patientId',
        fetchOpenTasksForParentType: fetchAllOpenTasksForParentType,
        onQuickAddTask: openQuickAddTask,
        onOpenDetails: showDetails,
        onLoadMoreClosed: loadMoreClosedItems,
    });
}

/**
 * Renders the Project WIP Kanban board.
 * @param {HTMLElement} board Board container.
 * @returns {Promise<void>}
 */
export async function renderProjectWIPKanban(board) {
    return renderParentWIPKanbanBoard(board, {
        viewKey: 'projects',
        itemType: 'project',
        parentType: 'project',
        items: appData?.projects || [],
        renderCard: (project, openTasks) => createProjectCard_WIP(project, openTasks),
        quickAddDatasetKey: 'projectId',
        fetchOpenTasksForParentType: fetchAllOpenTasksForParentType,
        onQuickAddTask: openQuickAddTask,
        onOpenDetails: showDetails,
        onLoadMoreClosed: loadMoreClosedItems,
    });
}

/**
 * Renders the Asset WIP Kanban board.
 * @param {HTMLElement} board Board container.
 * @returns {Promise<void>}
 */
export async function renderAssetWIPKanban(board) {
    return renderParentWIPKanbanBoard(board, {
        viewKey: 'assets',
        itemType: 'asset',
        parentType: 'asset',
        items: appData?.assets || [],
        renderCard: (asset, openTasks) => createAssetCard_WIP(asset, openTasks),
        quickAddDatasetKey: 'assetId',
        fetchOpenTasksForParentType: fetchAllOpenTasksForParentType,
        onQuickAddTask: openQuickAddTask,
        onOpenDetails: showDetails,
        onLoadMoreClosed: loadMoreClosedItems,
    });
}
